<template>
  <div>
    <div v-if="currentScreenType === 'is_done'">
      <div v-if="canRetakeQuiz">
        <h2>
          {{
            $t(
              `QUIZZES.TITLE.YOU_CAN_RETAKE_THIS_${quizOrQuestionnaire(
                isGradable
              )}`
            )
          }}
        </h2>        <p class="mt-3">
          {{
            $t(
              `QUIZZES.TEXT.WOULD_YOU_LIKE_TO_RETAKE_THIS_${quizOrQuestionnaire(
                isGradable
              )}`
            )
          }}
        </p>
        <HMButton class="mt-3" color="brand" @click="retake">
          {{ $t('GENERAL.BUTTON.RETAKE') }}
        </HMButton>
      </div>
      <div v-else>
        <h2>
          {{
            $t(
              `QUIZZES.TITLE.YOU_CANNOT_RETAKE_THIS_${quizOrQuestionnaire(
                isGradable
              )}`
            )
          }}
        </h2>        <p class="mt-3">
          {{ $t('QUIZZES.TEXT.PLEASE_CONTACT_YOUR_TEACHER_FOR_FURTHER_INFO') }}
        </p>
        <HMButton class="mt-3" color="brand" @click="$router.go(-1)">
          {{ $t('GENERAL.BUTTON.GO_BACK') }}
        </HMButton>
      </div>
    </div>

    <QuizStartScreen
      @start="start"
      v-if="currentScreenType === 'start'"
      :quiz="quizInfo"
      :loading="inProcess"
      :disabled="inProcess"
    />
    <div class="row" v-else-if="currentScreenType === 'questions'">
      <div class="col-12">
        <h3 v-if="currentQuestion.question_title">
          {{ currentQuestion.question_title }}
        </h3>
        <HMCustomContent
          v-if="currentQuestion.question_text"
          :json="currentQuestion.question_text"
          class="htms-editor"
        />
      </div>
      <div
        v-for="answer in currentQuestion.answers"
        :key="answer.id"
        class="col-12"
        :class="{
          'col-md-3': currentQuestion.question_type === 'single_image',
          'col-md-4': currentQuestion.question_type === 'file_upload',
        }"
      >
        <HMCheckBoxGroupStudent
          v-if="answer.answer_type === 'check'"
          :input-value="answer.answer_label"
          :placeholder="$t('QUIZZES.PLACEHOLDER.TYPE_ANSWER')"
          :modelValue="answer.is_selected"
          @update:modelValue="is_selected => toggleAnswerIsCorrect(is_selected, answer.id)"
        />
        <QuizUploadFile
          v-model="answer.uploads"
          :multiple="currentQuestion.allow_multiple_uploads"
          v-else-if="answer.answer_type === 'upload'"
          @uploading="status => (inProcess = status)"
        />
        <QuizDynamicHeightComponent
          v-else-if="answer.answer_type === 'text'"
          v-model="answer.text"
        />
        <HMImageCheckBoxGroup
          v-else-if="answer.answer_type === 'image_check'"
          :modelValue="answer.is_selected"
          @update:modelValue="is_selected => toggleAnswerIsCorrect(is_selected, answer.id)"
          :img="answer.answer_image"
          :label="answer.answer_label"
        />
        <HMOpinionScale
          v-else-if="answer.answer_type === 'scale'"
          v-model="answer.range"
          :range="answer.initial_range"
          :startText="answer.start_text"
          :endText="answer.end_text"
        />
      </div>
      <div class="col-12 mt-4">
        <HMButton
          v-if="!isLastQuestion"
          color="brand"
          @click="next"
          :loading="inProcess"
          :disabled="inProcess"
        >
          {{ $t('QUIZZES.BUTTON.NEXT_QUESTION') }}
        </HMButton>
        <HMButton
          v-else
          color="brand"
          @click="finish(quizId)"
          :loading="inProcess"
          :disabled="inProcess"
        >
          {{ $t('QUIZZES.BUTTON.FINISH_SUBMIT_QUIZ') }}
        </HMButton>
      </div>
    </div>
    <QuizFinishScreen
      v-else-if="currentScreenType === 'finish'"
      :solvedQuiz="solvedQuiz"
      @close="$emit('finish', solvedQuiz)"
      :loading="inProcess"
      :disabled="inProcess"
    />
  </div>
</template>

<script lang="ts">
import QuizStartScreen from '@/components/quizzes/QuizStartScreen.vue';
import QuizFinishScreen from '@/components/quizzes/QuizFinishScreen.vue';
import QuizUploadFile from '@/components/quizzes/QuizUploadFile.vue';
import QuizDynamicHeightComponent from '@/components/quizzes/QuizDynamicHeightComponent.vue';
import { errorToMessage } from '@/utils/errors';
import { quizOrQuestionnaire } from '@/utils/quizzes';
import { useStudentQuizStore } from '@/stores';

export default {
  components: {
    QuizStartScreen,
    QuizFinishScreen,
    QuizUploadFile,
    QuizDynamicHeightComponent,
  },
  props: {
    studioURL: {
      type: String,
      required: true,
    },
    quizId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentScreenType: null,
      solvedQuiz: null,
      quizInfo: null,
      currentQuestionIndex: 0,
      inProcess: false,
    };
  },
  computed: {
    currentQuestion() {
      if (this.solvedQuiz) {
        return this.solvedQuiz.quiz.quiz_questions[this.currentQuestionIndex];
      } else return null;
    },
    isLastQuestion() {
      const questionsNum = this.solvedQuiz?.quiz?.quiz_questions?.length;
      return this.currentQuestionIndex === questionsNum - 1;
    },
    canRetakeQuiz() {
      return this.quizInfo?.allow_retaking_quiz;
    },
    isGradable() {
      return this.quizInfo?.is_gradable;
    },
  },
  methods: {
    quizOrQuestionnaire,
    async start() {
      try {
        this.inProcess = true;
        const solvedQuiz = await useStudentQuizStore().startQuiz({
          studioURL: this.studioURL,
          quizId: this.quizId,
        });

        solvedQuiz.quiz.quiz_questions.forEach(question => {
          question.answers.forEach(answer => {
            answer.is_selected = false;
            answer.uploads = [];
            answer.text = '';
            answer.initial_range = answer.range;
            answer.range = null;
          });
        });

        this.solvedQuiz = solvedQuiz;

        this.setCurrentScreenType('questions');
        this.$emit('solvedQuiz', this.solvedQuiz);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t('QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ')}: ${error}`
          ),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    setCurrentScreenType(type) {
      this.currentScreenType = type;
      this.$emit('screenTypeChanged', type);
    },
    getSolvedQuiz(solvedQuizId: number) {
      try {
        useStudentQuizStore().startQuiz({
          studioURL: this.studioURL,
          solvedQuizId,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t('QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ')}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    answerQuizQuestion(data: StudentQuizAnswerRrequest) {
      try {
        return useStudentQuizStore().answerQuizQuestion({
          studioURL: this.studioURL,
          solvedQuizId: this.solvedQuiz?.id,
          data,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t(
              'QUIZZES.NOTIFICATIONS.FAILED_TO_ANSWER_QUIZ_QUESTION'
            )}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    async retake() {
      try {
        this.inProcess = true;
        this.solvedQuiz = await useStudentQuizStore().retakeQuiz({
          studioURL: this.studioURL,
          quizId: this.quizId,
        });
        this.setCurrentScreenType('start');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: 'Failed to retake the quiz',
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    async finish() {
      try {
        this.inProcess = true;
        await this.saveAnswers();
        this.solvedQuiz = await useStudentQuizStore().finishQuiz({
          studioURL: this.studioURL,
          solvedQuizId: this.solvedQuiz?.id,
        });
        this.setCurrentScreenType('finish');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t('QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ')}: ${error}`
          ),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    async saveAnswers() {
      const selectedAnswers = this.currentQuestion.answers.filter(
        answer =>
          answer.is_selected ||
          ['text', 'upload', 'scale'].includes(answer.answer_type)
      );
      const answers = await Promise.all(
        selectedAnswers.map(answer => this.answerQuizQuestion(answer))
      );

      const firstAnswer = this.currentQuestion.answers?.[0];
      if (firstAnswer && firstAnswer.answer_type === 'upload') {
        await Promise.all(
          firstAnswer.uploads.map(file =>
            this.saveAnswerFile(answers[0].id, file.id)
          )
        );
      }

      return answers;
    },
    saveAnswerFile(solvedQuizAnswerId, fileId) {
      try {
        const { studioURL } = this;
        return useStudentQuizStore().addSolvedQuizAnswerFile({
          studioURL,
          solvedQuizId: this.solvedQuiz?.id,
          solvedQuizAnswerId,
          fileId,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(
            error.response,
            `${this.$t(
              'QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ_ANSWER_FILE'
            )}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    async next() {
      try {
        this.inProcess = true;
        await this.saveAnswers();
        ++this.currentQuestionIndex;
        this.$emit('currentQuestionIndex', this.currentQuestionIndex + 1);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR') as string,
          text: errorToMessage(error.response),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    toggleAnswerIsCorrect(is_selected, answerId) {
      const multiSelect =
        this.currentQuestion.question_type === 'multiple_choice';

      this.currentQuestion.answers.forEach(answer => {
        if (answer.id === answerId) {
          answer.is_selected = is_selected;
        } else if (!multiSelect) {
          answer.is_selected = false;
        }
      });
    },
    onCheckboxGroupInputChanged(value, answer) {
      answer.answer_label = value;
    },
  },
  async mounted() {
    try {
      const studioURL = this.studioURL;
      const quizId = this.quizId;
      useStudentQuizStore().viewQuiz({
        studioURL,
        quizId,
      });
      this.quizInfo = await useStudentQuizStore().getQuiz({
        studioURL,
        quizId,
      });
      const type = this.quizInfo.is_done ? 'is_done' : 'start';
      this.setCurrentScreenType(type);
      this.$emit('quizInfo', this.quizInfo);
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
        text: errorToMessage(
          error.response,
          `${this.$t('QUIZZES.NOTIFICATIONS.FAILED_TO_LOAD_QUIZ')}: ${error}`
        ),
        duration: -1,
      });
    }
  },
};
</script>

<style lang="scss">
@import '@/styles/components-variables.scss';
.htms-editor {
  color: $dark-gray;
}
</style>
